import { Platform, Text as RNText, type TextStyle, View, type ViewStyle } from 'react-native';
import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';

export interface ChartTooltipProps {
  value: number | string;
  label?: string;
  prefix?: string;
  suffix?: string;
  accentColor?: string;
  decimals?: number;
}

function formatValue(value: number | string, decimals?: number): string {
  if (typeof value === 'string') return value;
  const opts: Intl.NumberFormatOptions =
    typeof decimals === 'number'
      ? { minimumFractionDigits: decimals, maximumFractionDigits: decimals }
      : { maximumFractionDigits: 2 };
  return value.toLocaleString('en-US', opts);
}

export function ChartTooltip({
  value,
  label,
  prefix = '',
  suffix = '',
  accentColor,
  decimals,
}: Readonly<ChartTooltipProps>) {
  const glowColor = accentColor ?? colors.glow.cyan;

  const containerStyle: ViewStyle = {
    backgroundColor: colors.background.elevated,
    borderColor: colors.border.subtle,
    borderWidth: 1,
    borderRadius: tokens.radius.md,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
    marginBottom: 6,
    minWidth: 72,
  };

  const platformStyle = Platform.select<ViewStyle>({
    web: {
      // backdropFilter is web-only; provides true frosted-glass effect
      ...({
        backdropFilter: 'blur(14px) saturate(140%)',
        WebkitBackdropFilter: 'blur(14px) saturate(140%)',
        boxShadow: `0 6px 18px ${glowColor}, 0 2px 6px rgba(0, 0, 0, 0.32)`,
      } as ViewStyle),
    },
    ios: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.32,
      shadowRadius: 12,
    },
    android: {
      elevation: 6,
    },
    default: {},
  });

  const labelStyle: TextStyle = {
    color: colors.text.tertiary,
    fontSize: tokens.fontSize.xs,
    fontWeight: '600',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    marginBottom: 2,
  };

  const valueStyle: TextStyle = {
    color: colors.text.primary,
    fontSize: tokens.fontSize.sm,
    fontWeight: '700',
    letterSpacing: -0.2,
  };

  return (
    <View style={[containerStyle, platformStyle]}>
      {label ? <RNText style={labelStyle}>{label}</RNText> : null}
      <RNText style={valueStyle} numberOfLines={1}>
        {`${prefix}${formatValue(value, decimals)}${suffix}`}
      </RNText>
    </View>
  );
}
